/**
 * Reading the API's error envelope, without React.
 *
 * Split out of `error-state.tsx` so the notices that branch on an error code
 * (`RequesterUnresolvedNotice`) can share it without an import cycle.
 */

/**
 * The API's error envelope, reduced to the four things a UI needs.
 *
 * Deliberately duck-typed rather than imported from `@/lib/api`: the generated
 * `ErrorResponse` type arrives with the backend schema, and the shell must
 * compile before that lands. `@hey-api`'s error interceptor (see
 * `src/lib/api/client.ts`) throws `{ status, body }`, where `body` is the
 * envelope — so both layers are unwrapped here.
 */
export interface NormalisedApiError {
  /** HTTP status, or `0` for a transport failure, or `null` if not an API error. */
  status: number | null;
  /** Stable machine code from the envelope, e.g. `TICKET_NOT_FOUND`. */
  code: string | null;
  /** Correlation id to quote to IT. */
  traceId: string | null;
  /** English developer text. Never shown to a user — logs and dev tools only. */
  detail: string | null;
  /** The envelope's string `params`, for interpolating into the code's copy. */
  params: Record<string, string>;
}

function readString(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Pulls `{ status, code, traceId, detail }` out of whatever a query rejected with. */
export function normaliseApiError(error: unknown): NormalisedApiError {
  const empty: NormalisedApiError = {
    status: null,
    code: null,
    traceId: null,
    detail: null,
    params: {},
  };
  if (!isRecord(error)) {
    return error instanceof Error ? { ...empty, detail: error.message } : empty;
  }

  const status = typeof error["status"] === "number" ? error["status"] : null;
  const body = isRecord(error["body"]) ? error["body"] : error;

  return {
    status,
    code: readString(body, "code"),
    traceId: readString(body, "trace_id"),
    detail: readString(body, "detail") ?? readString(body, "message"),
    params: readParams(body),
  };
}

/** The envelope's `params`, keeping only string values. */
function readParams(body: Record<string, unknown>): Record<string, string> {
  const raw = body["params"];
  if (!isRecord(raw)) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(raw).filter((entry): entry is [string, string] => typeof entry[1] === "string"),
  );
}

// ─── "Ivanti has no record of you" ─────────────────────────────────────────

/** The write endpoints' 409 code when Ivanti has no record of the signed-in person. */
export const REQUESTER_NOT_REGISTERED = "REQUESTER_NOT_REGISTERED";

/** The list `result.reason` for the same condition on a read. */
export const REQUESTER_UNRESOLVED = "REQUESTER_UNRESOLVED";

/**
 * True when a rejection means "we couldn't find your IT profile".
 *
 * Accepts either code: writes answer `REQUESTER_NOT_REGISTERED`, and a stream
 * or envelope that borrowed the list's `REQUESTER_UNRESOLVED` means the same.
 */
export function isRequesterUnresolvedError(error: unknown): boolean {
  if (error === null || error === undefined) {
    return false;
  }
  const { code } = normaliseApiError(error);
  return code === REQUESTER_NOT_REGISTERED || code === REQUESTER_UNRESOLVED;
}

/**
 * True when a list result is short because the requester could not be resolved.
 *
 * Any non-complete state counts: the list is `UNAVAILABLE` when nothing could
 * be read, and may be `PARTIAL` when some tickets were still found by email.
 */
export function isRequesterUnresolvedResult(
  result: { state?: string | null; reason?: string | null } | null | undefined,
): boolean {
  return (
    result !== null &&
    result !== undefined &&
    result.reason === REQUESTER_UNRESOLVED &&
    result.state !== "COMPLETE"
  );
}

// ─── Failures worth a way out ──────────────────────────────────────────────

/**
 * Codes after which a write (a ticket, a request, a comment) may simply keep
 * failing, so "try again" alone leaves the person stuck. `TIMEOUT` is not a
 * code the API sends today; it is listed so a client-side timeout that adopts
 * it is covered.
 */
const ESCALATE_WRITE_CODES: ReadonlySet<string> = new Set([
  "PROVIDER_UNAVAILABLE",
  "VALIDATION_FAILED",
  "TIMEOUT",
  "INTERNAL_ERROR",
  "SERVICE_STARTING",
  "OFFERING_SUBMIT_REJECTED",
  "RATE_LIMITED",
  "USER_QUOTA_EXCEEDED",
  "ATTACHMENT_STORE_FULL",
]);

/**
 * True when a failed write should offer the Global IT Helpdesk contact.
 *
 * Also true for a failure with no code at all — the network dropped, or the
 * response was not our envelope — since that is exactly when nothing on the
 * page can say what went wrong.
 */
export function shouldOfferHelpdeskContact(error: unknown): boolean {
  if (error === null || error === undefined) {
    return false;
  }
  const { status, code } = normaliseApiError(error);
  if (status === 0 || code === null) {
    return true;
  }
  return ESCALATE_WRITE_CODES.has(code);
}

// ─── Logged failures (8 Oct) ───────────────────────────────────────────────

/** Characters of the trace id a person is shown, matching the server's `ref`. */
export const SHORT_REF_CHARS = 8;

/** The short reference a person can quote: the trace id's first characters. */
export function shortRef(traceId: string | null): string | null {
  return traceId === null ? null : traceId.slice(0, SHORT_REF_CHARS).toUpperCase();
}

/**
 * The reference of a failure the server logged for IT, or `null`.
 *
 * Rusty, 8 Oct: when the HelpDesk (or Ivanti) fails a person, the red box
 * reassures — "we've logged it and IT will look into it" — with a short
 * reference, and never shows Ivanti's reason or a code. The server marks such
 * an error with `params.logged = "true"` and `params.ref`.
 */
export function loggedRef(error: unknown): string | null {
  if (error === null || error === undefined) {
    return null;
  }
  const { params, traceId } = normaliseApiError(error);
  if (params["logged"] !== "true") {
    return null;
  }
  return params["ref"] ?? shortRef(traceId) ?? "";
}
